import { Box, Column, Expanded, Row, Text } from '@st/document-rendering'
import { CalculatorTapeAnnotation, CalculatorTapeLineItem } from '@st/sdk'
import { memo, ReactNode } from 'react'
import { CalculatorTapeLayout, getCalculatorTapeLayout } from './calculator-tape-layout'

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export const CalculatorTapeView = memo(CalculatorTapeViewBase)

type Props = { annotation: Omit<CalculatorTapeAnnotation, 'id' | 'time'> }

function CalculatorTapeViewBase({ annotation }: Props) {
  const layout = getCalculatorTapeLayout(annotation)

  return (
    <CalculatorTapeScaffold
      layout={layout}
      width={annotation.bounds.width}
      height={annotation.bounds.height}
      title={annotation.title ?? ''}
      lineItems={annotation.lineItems}
      total={annotation.total}
      renderRow={(item, idx) => (
        <LineItemRow
          key={idx}
          layout={layout}
          description={item.description ?? ''}
          amount={item.amount}
        />
      )}
    />
  )
}

// Tape rows have a fixed height and retain their entered line structure.
// Wrapping would require changing the row and annotation bounds together;
// softWrap=false preserves that layout policy rather than changing font metrics.
function CalculatorTapeScaffold({
  layout,
  width,
  height,
  title,
  lineItems,
  total,
  renderRow,
  border = true
}: {
  layout: CalculatorTapeLayout
  width: number
  height: number
  title: string
  lineItems: CalculatorTapeLineItem[]
  total: string
  renderRow: (item: CalculatorTapeLineItem, index: number) => ReactNode
  border?: boolean
}) {
  return (
    <Box
      width={width}
      height={height}
      padding={layout.container.padding}
      border={border ? { color: '#000000', width: 2 } : undefined}
      backgroundColor="#FFFFFF"
    >
      <Column>
        {/* Title */}
        <Box height={layout.title.height} alignment="centerLeft">
          <Text fontSize={layout.title.fontSize} fontFamily="Helvetica" softWrap={false}>
            {title}
          </Text>
        </Box>

        {/* Spacer before first divider */}
        <Box height={layout.dividerHeight} />

        {/* Line items */}
        {lineItems.map((li, idx) => renderRow(li, idx))}

        {/* Divider before total */}
        <HDivider height={layout.dividerHeight} />

        {/* Total row */}
        <TotalRow layout={layout} total={total} />
      </Column>
    </Box>
  )
}

function LineItemRow({
  layout,
  description,
  amount
}: {
  layout: CalculatorTapeLayout
  description: string
  amount: string
}) {
  const isNegative = parseFloat(amount) < 0

  return (
    <Box height={layout.row.height}>
      <Row gap={layout.column.gap} crossAxisAlignment="center">
        <Expanded flex={layout.column.leftFlex}>
          <Text fontSize={layout.row.fontSize} fontFamily="Helvetica" softWrap={false}>
            {description}
          </Text>
        </Expanded>
        <Expanded flex={layout.column.rightFlex}>
          <Text
            fontSize={layout.row.fontSize}
            fontFamily="Helvetica"
            softWrap={false}
            textAlign="right"
            color={isNegative ? '#CB3A31' : '#000000'}
          >
            {amount}
          </Text>
        </Expanded>
      </Row>
    </Box>
  )
}

function TotalRow({ layout, total }: { layout: CalculatorTapeLayout; total: string }) {
  return (
    <Box height={layout.row.height}>
      <Row crossAxisAlignment="center">
        <Expanded flex={layout.column.leftFlex}>
          <Text
            fontSize={layout.row.fontSize}
            fontFamily="Helvetica"
            softWrap={false}
            fontWeight="bold"
          >
            Total
          </Text>
        </Expanded>
        <Expanded flex={layout.column.rightFlex}>
          <Text
            fontSize={layout.row.fontSize}
            fontFamily="Helvetica"
            softWrap={false}
            fontWeight="bold"
            textAlign="right"
          >
            {total}
          </Text>
        </Expanded>
      </Row>
    </Box>
  )
}

function HDivider({ height }: { height: number }) {
  return (
    <Column crossAxisAlignment="stretch" mainAxisSize="min">
      <Box height={height / 2 - 0.5} />
      <Box height={1} backgroundColor="#000000" />
      <Box height={height / 2 - 0.5} />
    </Column>
  )
}
